import React, { useEffect, useMemo, useState } from "react";
import {
  useGame,
  usePlayer,
  usePlayers,
  useStageTimer,
} from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import { LossFrameDemandHistoryVisualization } from "../stage2/Stage2ResultStage";
import {
  DollarDemandInput,
  clampDollarValue,
  formatDollarDemandDisplay,
  integerDigitCount,
  parseDollarDemandDisplay,
} from "./DollarDemandInput";
import { DemandAgainstPotChart } from "./NegotiationDonutCharts";
import {
  getCounterpartCopy,
  getNegotiationTermCopy,
  isLossFrameDesign,
  lossOfferFromDemand,
} from "../payoffFrame";

const MIN_EXPLANATION_CHARACTERS = 50;
const ZERO_OFFER_WARNING_SECONDS = 60;
const INITIAL_OFFER_INPUT_ID = "initial-offer-amount";

export function NegotiationStage() {
  const game = useGame();
  const player = usePlayer();
  const players = usePlayers();
  const stageTimer = useStageTimer();
  const design = game.get("design") || {};
  const stage1 = game.get("stage1") || {};
  const stage2 = game.get("stage2") || {};
  const pieDollars = Math.max(0, Number(stage2.teamWinningsBeforeAdjustment ?? 0));
  const maxDemandDollars = pieDollars;
  const integerDigits = integerDigitCount(maxDemandDollars);
  const lossFrame = isLossFrameDesign(design);
  const termCopy = getNegotiationTermCopy(design);
  const counterpart = getCounterpartCopy(design);

  const testMode = design.testMode === true;

  const initialAsk = parseDollarDemandDisplay(
    player.stage.get("initialDemandDraftDollars")
  );
  const initialFrameValue = Number.isFinite(initialAsk)
    ? clampDollarValue(initialAsk, maxDemandDollars)
    : Number.NaN;
  const initialExplanation = player.stage.get("initialDemandExplanationDraft");
  const [proposalInput, setProposalInput] = useState(
    formatDollarDemandDisplay(initialFrameValue, maxDemandDollars, integerDigits)
  );
  const [explanationInput, setExplanationInput] = useState(
    typeof initialExplanation === "string" ? initialExplanation : ""
  );
  const [demandWasAltered, setDemandWasAltered] = useState(
    player.stage.get("initialDemandDraftAltered") === true
  );
  const [showOfferError, setShowOfferError] = useState(false);
  const [showExplanationError, setShowExplanationError] = useState(false);
  const [previewZeroOfferWarning, setPreviewZeroOfferWarning] = useState(false);

  const allSubmitted = useMemo(
    () => players.every((p) => p.stage.get("submit")),
    [players]
  );
  const partnerSubmitted = useMemo(
    () => players.some((p) => p.id !== player.id && p.stage.get("submit")),
    [players, player.id]
  );

  useEffect(() => {
    const parsedDemand = parseDollarDemandDisplay(proposalInput);
    if (!Number.isFinite(parsedDemand)) {
      player.stage.set("initialDemandDraftDollars", null);
      player.stage.set("initialLossOfferDraftDollars", null);
      return;
    }

    const canonicalDemand = clampDollarValue(parsedDemand, maxDemandDollars);
    const nextDisplay = formatDollarDemandDisplay(
      canonicalDemand,
      maxDemandDollars,
      integerDigits
    );
    if (nextDisplay !== proposalInput) {
      setProposalInput(nextDisplay);
    }
    player.stage.set("initialDemandDraftDollars", canonicalDemand);
    player.stage.set(
      "initialLossOfferDraftDollars",
      lossOfferFromDemand(canonicalDemand, maxDemandDollars)
    );
  }, [integerDigits, maxDemandDollars, player, proposalInput]);

  useEffect(() => {
    player.stage.set("initialDemandExplanationDraft", explanationInput);
  }, [explanationInput, player]);

  function handleSkipWithRandomProposal() {
    if (!testMode) {
      return;
    }

    const randomFrameValue = Number((Math.random() * maxDemandDollars).toFixed(2));
    const canonicalDemand = randomFrameValue;
    player.stage.set("initialDemandDraftDollars", canonicalDemand);
    player.stage.set(
      "initialLossOfferDraftDollars",
      lossOfferFromDemand(canonicalDemand, maxDemandDollars)
    );
    setProposalInput(formatDollarDemandDisplay(randomFrameValue, maxDemandDollars, integerDigits));
    setDemandWasAltered(true);
    setShowOfferError(false);
    player.stage.set("initialDemandDraftAltered", true);
  }

  function handleProposalInputChange(nextValue) {
    setDemandWasAltered(true);
    player.stage.set("initialDemandDraftAltered", true);
    setProposalInput(nextValue);
    if (Number.isFinite(parseDollarDemandDisplay(nextValue))) {
      setShowOfferError(false);
    }
  }

  function handleDemandVisualizationChange(nextDemand) {
    const safeDemand = clampDollarValue(nextDemand, maxDemandDollars);
    setDemandWasAltered(true);
    player.stage.set("initialDemandDraftAltered", true);
    setProposalInput(
      formatDollarDemandDisplay(safeDemand, maxDemandDollars, integerDigits)
    );
    setShowOfferError(false);
  }

  function handleClearOffer() {
    setProposalInput(
      formatDollarDemandDisplay(Number.NaN, maxDemandDollars, integerDigits)
    );
    setDemandWasAltered(false);
    setShowOfferError(false);
    player.stage.set("initialDemandDraftDollars", null);
    player.stage.set("initialLossOfferDraftDollars", null);
    player.stage.set("initialDemandSubmittedDollars", null);
    player.stage.set("initialLossOfferSubmittedDollars", null);
    player.stage.set("initialDemandDraftAltered", false);
    player.set("negotiationDraftOfferDollars", null);
    player.set("negotiationDraftDemandDollars", null);
  }

  function handleSubmitInitialDemand(event) {
    event.preventDefault();

    const parsedDemand = parseDollarDemandDisplay(proposalInput);
    const offerIsValid = Number.isFinite(parsedDemand);
    const explanationIsValid =
      explanationInput.trim().length >= MIN_EXPLANATION_CHARACTERS;
    setShowOfferError(!offerIsValid);
    setShowExplanationError(!explanationIsValid);

    if (!offerIsValid || !explanationIsValid) {
      return;
    }

    const safeFrameValue = clampDollarValue(parsedDemand, maxDemandDollars);
    const safeDemand = safeFrameValue;
    const safeLossOffer = lossOfferFromDemand(safeDemand, maxDemandDollars);
    setShowExplanationError(false);

    player.stage.set("initialDemandSubmittedDollars", safeDemand);
    player.stage.set("initialLossOfferSubmittedDollars", safeLossOffer);
    player.stage.set("initialDemandExplanation", explanationInput.trim());
    player.set("negotiationDraftOfferDollars", safeDemand);
    player.set("negotiationDraftDemandDollars", safeDemand);
    player.stage.set("submit", true);
  }

  function handleGoToAmountBox() {
    const input = document.getElementById(INITIAL_OFFER_INPUT_ID);
    input?.scrollIntoView({ block: "center" });
    input?.focus({ preventScroll: true });
  }

  function handleExplanationChange(nextValue) {
    setExplanationInput(nextValue);
    if (
      showExplanationError &&
      nextValue.trim().length >= MIN_EXPLANATION_CHARACTERS
    ) {
      setShowExplanationError(false);
    }
  }

  function handleCancelInitialDemand() {
    if (!player.stage.get("submit")) {
      return;
    }

    const hasPartnerSubmitted = players.some(
      (p) => p.id !== player.id && p.stage.get("submit")
    );
    if (hasPartnerSubmitted) {
      return;
    }

    player.stage.set("submit", false);
  }

  const alreadySubmitted = Boolean(player.stage.get("submit"));
  const parsedCurrentFrameValue = parseDollarDemandDisplay(proposalInput);
  const currentFrameValue = Number.isFinite(parsedCurrentFrameValue)
    ? clampDollarValue(parsedCurrentFrameValue, maxDemandDollars)
    : Number.NaN;
  const currentDemand = currentFrameValue;

  // With no amount entered, the server counts this initial offer as $0.
  const lowTimeRemaining =
    typeof stageTimer?.remaining === "number" &&
    stageTimer.remaining < ZERO_OFFER_WARNING_SECONDS * 1000;
  const showZeroOfferWarning =
    !Number.isFinite(currentDemand) &&
    (lowTimeRemaining || (testMode && previewZeroOfferWarning));

  useEffect(() => {
    if (
      showZeroOfferWarning &&
      player.stage.get("initialDemandZeroWarningShown") !== true
    ) {
      player.stage.set("initialDemandZeroWarningShown", true);
    }
  }, [player, showZeroOfferWarning]);

  return (
    <div className="max-w-4xl mx-auto px-8 py-6 space-y-6">
      {showZeroOfferWarning ? (
        <div
          role="alert"
          className="sticky top-0 z-40 flex flex-wrap items-center justify-between gap-4 rounded-md border-2 border-red-600 bg-red-50 p-4 shadow-lg"
        >
          <p className="text-2xl text-red-800">
            <strong>Less than {ZERO_OFFER_WARNING_SECONDS} seconds left.</strong>{" "}
            You haven't entered an amount. If you don't enter one, your{" "}
            {termCopy.term} will be <strong>$0</strong>, and{" "}
            <strong>
              you will ask to keep <u>nothing</u> for yourself
            </strong>.
          </p>
          <Button
            type="button"
            handleClick={handleGoToAmountBox}
            className="shrink-0 text-xl"
          >
            Go to the amount box
          </Button>
        </div>
      ) : null}
      <h2 className="text-4xl font-semibold text-gray-900">
          Submit and Explain {lossFrame ? termCopy.savingsLabel : termCopy.capitalizedTerm}
        </h2>
      <div className="rounded-md border border-gray-200 p-4 space-y-2">

        <p className="text-4xl text-gray-700">
          {lossFrame ? "Game 2 savings at stake" : "Game 2 winnings"}:{" "}
          <strong>${pieDollars.toFixed(2)}</strong>.
        </p>

        <p className="text-2xl text-gray-700">
          {lossFrame ? (
            <>
              You can enter your <b>initial {termCopy.term}</b>: the amount of the <b>Game 2
              savings</b> you want to keep.
            </>
          ) : (
            <>
              Enter your <b>initial dollar {termCopy.term}</b>: the amount of the{" "}
              <b>Game 2 winnings you want to keep for yourself</b>, and explain{" "}
              your reasoning.
            </>
          )}
        </p>

        <p className="text-2xl text-gray-700">
          {lossFrame ? (
            <>
              If your {termCopy.term} plus their {termCopy.term} is{" "}
              <strong>${pieDollars.toFixed(2)}</strong> or <b>less</b>, your{" "}
              {termCopy.pluralTerm} fit within the saved amount and each will <b>receive their requested share</b>.
            </>
          ) : (
            <>
              If your {termCopy.term} plus their {termCopy.term} is{" "}
              <strong>${pieDollars.toFixed(2)}</strong> or <b>less</b>, you will{" "}
              <b>receive your {termCopy.term}</b>.
            </>
          )}
        </p>

        <p className="text-2xl text-gray-700">
          {lossFrame ? (
            <>
              If the combined {termCopy.pluralTerm} are <b>more</b> than{" "}
              <strong>${pieDollars.toFixed(2)}</strong>, your {termCopy.pluralTerm}{" "}
              are incompatible, so you will <b>discuss</b> and{" "}
              submit a <b>final {termCopy.term}</b>. <u><b>Note</b></u>: you are negotiating over a shared pot of savings.
            </>
          ) : (
            <>
              If the combined {termCopy.pluralTerm} are <b>more</b> than{" "}
              <strong>${pieDollars.toFixed(2)}</strong>, you will <b>discuss</b>{" "}
              and submit a <b>final {termCopy.term}</b>.
            </>
          )}
        </p>

      </div>

      <form className="rounded-md border border-gray-200 p-4 space-y-4" onSubmit={handleSubmitInitialDemand}>
        <div className="space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <label htmlFor={INITIAL_OFFER_INPUT_ID} className="block text-2xl font-medium text-gray-900">
              Amount you want to keep ($):
            </label>
            <Button
              type="button"
              primary
              disabled={alreadySubmitted}
              handleClick={handleClearOffer}
              className="shrink-0 text-base"
            >
              Clear {termCopy.term}
            </Button>
          </div>
          <p className="text-xl text-gray-700">
            You can navigate between the digits using the arrow keys.
          </p>

          <DollarDemandInput
            id={INITIAL_OFFER_INPUT_ID}
            value={proposalInput}
            maxDollars={maxDemandDollars}
            onChange={handleProposalInputChange}
            disabled={alreadySubmitted}
            className={`appearance-none block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-empirica-500 focus:border-empirica-500 text-3xl${
              showZeroOfferWarning ? " zero-offer-warning-flash" : ""
            }`}
          />
        </div>

        {lossFrame ? (
          <LossFrameDemandHistoryVisualization
            stage1={stage1}
            stage2={stage2}
            design={design}
            player={player}
            players={players}
            demand={currentDemand}
            demandLabel={termCopy.savingsLabel}
            demandColor="#16a34a"
            demandLegendValueClassName="text-green-700"
            savingsLeftOverLabel={
              demandWasAltered ? "Potential Losses" : "Savings at Stake"
            }
            showZeroValueLegendItems={demandWasAltered}
            demandSelectionTitle={termCopy.selectionTitle}
            onDemandChange={
              alreadySubmitted ? undefined : handleDemandVisualizationChange
            }
          />
        ) : (
          <DemandAgainstPotChart
            title={termCopy.yourWinningsLabel}
            demand={currentDemand}
            pot={pieDollars}
            color="#818cf8"
            demandLabel={termCopy.yourWinningsLabel}
            remainingLabel="Pay Left Over"
            overageLabel={`${termCopy.capitalizedTerm} Above Winnings`}
            totalLabel="winnings"
            legendSide
            legendTextClassName="text-2xl"
            centerValueClassName="fill-blue-700 text-base font-semibold"
            centerTotalClassName="fill-gray-900 text-base font-semibold"
            showPrimaryZeroValueLegendItems
            showSubtitle={false}
            showTitle={false}
            chartOrderLabels={[
              "Pay Left Over",
              termCopy.yourWinningsLabel,
              `${termCopy.capitalizedTerm} Above Winnings`,
            ]}
          />
        )}

        <div className="space-y-2">
          <label className="block text-3xl font-medium text-gray-900">
            Explain your {termCopy.capitalizedTerm}:
          </label>
          
          <p className="text-2xl text-gray-700">
            Your {counterpart.term} will see this before discussion.
          </p>
          <textarea
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-empirica-500 focus:border-empirica-500 text-xl"
            rows={5}
            placeholder={`Write your reasoning. Your ${counterpart.term} will see this before discussion.`}
            value={explanationInput}
            onChange={(e) => handleExplanationChange(e.target.value)}
            disabled={alreadySubmitted}
          />
          {showExplanationError ? (
            <p className="text-sm text-red-600">
              Not enough explanation for your {termCopy.term}
            </p>
          ) : null}
        </div>

        <div className="flex items-center space-x-4">
          <Button type="submit" primary disabled={alreadySubmitted} className="text-xl">
            Submit {termCopy.capitalizedTerm}
          </Button>
          {showOfferError ? (
            <span className="text-sm text-red-600" role="alert">
              Must submit a valid {termCopy.term}.
            </span>
          ) : null}
          {alreadySubmitted && !partnerSubmitted ? (
            <Button type="button" handleClick={handleCancelInitialDemand}>
              Cancel
            </Button>
          ) : null}
          {alreadySubmitted && !allSubmitted ? (
            <span className="text-sm text-gray-500">
              Waiting for {counterpart.term}…
            </span>
          ) : null}
        </div>
      </form>

      {testMode ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-center justify-between">
          <span className="text-xs text-amber-800">
            Test mode is on. You can set a random private proposal.
          </span>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              handleClick={handleSkipWithRandomProposal}
            >
              Set random proposal (test)
            </Button>
            <Button
              type="button"
              handleClick={() => setPreviewZeroOfferWarning((shown) => !shown)}
            >
              {previewZeroOfferWarning ? "Hide" : "Show"} $0 warning (test)
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
